// src/components/superadmin/SuperAdminUserEdit.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { BiSave, BiX, BiUser, BiIdCard } from "react-icons/bi";
import { API_BASE_URL } from "../utils/urls.js"; // Ensure correct path
import LoadingSpinner from "../common/LoadingSpinner"; // Assuming you have this

const SuperAdminUserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    role_id: "",
    is_active: 1, // Default to active
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Fetch User Data and Roles
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMessage("");
    try {
      const token = localStorage.getItem("token");
      // Fetch user data and roles concurrently
      const [userRes, rolesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/super-admin/users/${id}`, {
          // Use super-admin GET endpoint
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/users/roles`, {
          // Use existing roles endpoint
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const userData = userRes.data;
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        phone_number: userData.phone_number || "",
        role_id: userData.role_id || "",
        is_active: userData.is_active ?? 1, // Use fetched active status, default to 1 if null/undefined
      });
      setRoles(rolesRes.data);
    } catch (err) {
      setError("Failed to load user data or roles.");
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value, // Handle checkbox for is_active
    }));
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      // Send PUT request to the super-admin update endpoint
      const response = await axios.put(
        `${API_BASE_URL}/super-admin/users/${id}`,
        formData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setMessage(response.data.message || "User updated successfully!");
      setTimeout(() => {
        navigate(`/super-admin/users/${id}`); // Navigate back to view page on success
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update user.");
      console.error("Update error:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const formatRoleName = (roleName = "") =>
    roleName
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  if (loading)
    return (
      <div className="p-10 flex justify-center">
        <LoadingSpinner />
      </div>
    );

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      {/* Back Link */}
      <Link
        to={`/super-admin/users/${id}`}
        className="text-blue-600 hover:underline mb-4 inline-block"
      >
        &larr; Back to User View
      </Link>

      <h1 className="text-2xl font-bold text-gray-800 mb-6">
        Edit User: {formData.first_name} {formData.last_name}
      </h1>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      {message && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
          {message}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-lg rounded-lg p-6 space-y-5"
      >
        {/* Basic Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="first_name"
              className="block text-sm font-medium text-gray-700"
            >
              First Name *
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              className="mt-1 p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label
              htmlFor="last_name"
              className="block text-sm font-medium text-gray-700"
            >
              Last Name *
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              className="mt-1 p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-gray-700"
          >
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="mt-1 p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="phone_number"
            className="block text-sm font-medium text-gray-700"
          >
            Phone Number
          </label>
          <input
            type="tel"
            id="phone_number"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            className="mt-1 p-2 border rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        {/* Role Selection */}
        <div>
          <label
            htmlFor="role_id"
            className="block text-sm font-medium text-gray-700"
          >
            Role *
          </label>
          <select
            id="role_id"
            name="role_id"
            value={formData.role_id}
            onChange={handleChange}
            required
            className="mt-1 p-2 border rounded-md w-full bg-gray-50 focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="" disabled>
              Select a role
            </option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {formatRoleName(role.role_name)}
              </option>
            ))}
          </select>
        </div>

        {/* Active Status Toggle */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="is_active"
            name="is_active"
            checked={formData.is_active === 1}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label
            htmlFor="is_active"
            className="ml-2 block text-sm font-medium text-gray-700"
          >
            User is Active
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate(`/super-admin/users/${id}`)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition"
          >
            <BiX className="inline w-4 h-4 mr-1" /> Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center"
          >
            <BiSave className="inline w-4 h-4 mr-1" />{" "}
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SuperAdminUserEdit;
